import { Flex, Popover, Text, UnstyledButton } from "@mantine/core";
import get from "lodash/get";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCompanyOptions } from "../../../api/hooks/customer/useCompanyOptions.ts";
import { AppRoute } from "../../../constants.ts";
import { RolePermission } from "../../../graphql/generated.ts";
import { getTabs as getARMTabs } from "../../../pages/ActivityManagementDetail/constants.tsx";
import { getTabs as getRMTabs } from "../../../pages/ClikDataDetails/constants.tsx";
import { useCurrentUserContext } from "../../../providers/CurrentUserProvider.tsx";
import { CustomerOption } from "../../../types/customer.ts";
import { getCompanyOptions } from "../../../utils/company.ts";
import { theme } from "../../../utils/mantine/theme.ts";
import { canViewCreditLimitMenu } from "../../../utils/permissions.ts";
import { hasPermission } from "../../../utils/user.ts";
import SvgChevronLeft from "../../Icons/ChevronLeft.tsx";
import SvgSearchSimple from "../../Icons/SearchSimple.tsx";
import { Select } from "../../Select.tsx";
import { useStyles } from "../QuickGuide.styles.ts";
import SidebarItem from "../Sidebar/SidebarItem/SidebarItem.tsx";
import { NavigationLink, NavigationNestedLink } from "../types.ts";

export const QuickGuide = () => {
  const { classes } = useStyles();

  const { user } = useCurrentUserContext();
  const navigate = useNavigate();

  const [{ data: customerOptionsResponse, fetching: customerOptionsFetching }] =
    useCompanyOptions();
  const customerOptions = customerOptionsResponse?.customers.data;

  const [customer, setCustomer] = useState<CustomerOption | null>(null);

  const links: (NavigationLink | NavigationNestedLink)[] = customer?.id
    ? [
        {
          label: "Risk Management",
          show:
            !!customer.clikDataId &&
            canViewCreditLimitMenu(user) &&
            hasPermission(user, RolePermission.CanViewClikData, true),
          nestedLinks: getRMTabs({
            isCustomerDirectorInfoEmpty: false,
            user,
          }).map((tab) => ({
            label: tab.label,
            url: `${AppRoute.ClikData}/${customer.clikDataId}?tab=${tab.value}`,
          })),
        },
        {
          label: "AR Management",
          show:
            hasPermission(user, RolePermission.CanViewCollection) ||
            hasPermission(user, RolePermission.CanViewActivityManagement),
          nestedLinks: getARMTabs(user).map((tab) => ({
            label: tab.label,
            url: `${AppRoute.ActivityManagement}/${customer.id}?tab=${tab.value}`,
          })),
        },
      ]
    : [];

  const handleOpenLink = useCallback(
    (link: NavigationLink | NavigationNestedLink) => {
      if (link.url) {
        navigate(link.url);
      }
    },
    [navigate]
  );

  const handleSelectCustomer = useCallback(
    (customerId: string) => {
      const customerOption = customerOptions?.find(
        ({ id }) => String(id) === customerId
      );
      if (customerOption) {
        setCustomer(customerOption);
      }
    },
    [customerOptions]
  );

  return (
    <Popover
      position="bottom"
      width="350"
      shadow="md"
      keepMounted={true}
      onClose={() => setCustomer(null)}
    >
      <Popover.Target>
        <UnstyledButton variant="disabled">
          <Flex direction="row" gap={6}>
            <SvgSearchSimple color={get(theme, "colors.golden.0")} />
            <Text color={get(theme, "colors.golden.0")}>Quick Guide</Text>
          </Flex>
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        {!customer?.id ? (
          <Flex p={16} w="100%">
            <Select
              w="100%"
              size="m"
              searchable
              placeholder="Select Customer Name"
              value={null}
              data={getCompanyOptions(customerOptions)}
              loading={customerOptionsFetching}
              onChange={handleSelectCustomer}
            />
          </Flex>
        ) : (
          <Flex
            className={classes.nestedLinkWrapper}
            p={16}
            w="100%"
            direction="column"
          >
            <UnstyledButton
              className={classes.nestedLinkWrapper}
              pb={10}
              onClick={() => setCustomer(null)}
            >
              <Flex direction="row" align="center">
                <SvgChevronLeft />
                <Text size="sm">Back</Text>
              </Flex>
            </UnstyledButton>
            {links
              .filter(({ show }) => !!show)
              .map((link, index) => (
                <SidebarItem
                  key={index + 1}
                  item={link}
                  isOpen={true}
                  onClick={() => handleOpenLink(link)}
                />
              ))}
          </Flex>
        )}
      </Popover.Dropdown>
    </Popover>
  );
};
